// CrystalClear Vaults — Vault Detail Page (#vault/onyx)

import { VAULT_CONFIGS, VAULT_ABI, ERC20_ABI, USDC_ADDRESS, USDC_DECIMALS, SHARE_DECIMALS, EXPLORER, HL_EXPLORER, APP_MODE, REBALANCE_HISTORY } from '../config.js';
import { getAddress, getSigner, getReadProvider, getUsdcBalance, getHypeBalance } from '../wallet.js';
import { zapEntryHtml, wireZapEntry, usdcHelpTip } from '../deposit-ui.js';
import { showTxModal, txModalSuccess, txModalError } from '../tx.js';
import { formatPerfPct, formatBuilderPct, computeWithdrawalFee } from '../vault-math.js';
import { isStealth, isDepositor, canBypassStealth, launchDateLabel, launchDateShort, startCountdown, xFollowUrl } from '../stealth-mode.js';
import { tzZone, tzLabel } from '../time.js';

function _getVault(key) {
  return VAULT_CONFIGS.find(v => v.key === key);
}

// Stealth view for a live vault — used when stealth is on AND the caller
// isn't a known depositor. Shows gemstone identity + countdown; hides stats,
// chart, deposit/withdraw forms, strategy info. initVaultDetail() swaps in
// the full live DOM when the wallet passes the depositor check.
function _renderStealthVaultDetail(vc) {
  return `
  <div class="flex flex-col items-center justify-center py-16 px-6 text-center">
    <a href="#vaults" class="absolute top-24 left-6 md:left-12 w-8 h-8 flex items-center justify-center rounded-lg bg-surface-container-high hover:bg-outline-variant/20 transition-colors">
      <span class="material-symbols-outlined text-secondary text-sm">arrow_back</span>
    </a>
    <div class="w-16 h-16 rounded-2xl ${vc.color.bg20} flex items-center justify-center mb-5 ${vc.color.shadow}">
      <span class="material-symbols-outlined ${vc.color.text} text-3xl">${vc.icon}</span>
    </div>
    <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface-container-low ghost-border mb-4">
      <span class="w-1.5 h-1.5 rounded-full bg-primary animate-ping"></span>
      <span class="text-[10px] uppercase tracking-widest text-primary/80 font-bold">Trading · Opens ${launchDateShort()}</span>
    </div>
    <h2 class="font-headline font-bold text-3xl text-on-surface mb-2">${vc.name}</h2>
    <p class="text-secondary/70 mb-8">${vc.subtitle}</p>
    <p class="text-on-surface-variant max-w-md mb-8">
      This vault is trading live today. Performance metrics and deposits
      unlock on <span class="text-primary">${launchDateLabel()}</span>.
    </p>
    <div id="vd-stealth-countdown" class="mb-10"></div>
    <a href="${xFollowUrl()}" target="_blank" rel="noopener"
       class="px-6 py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary-container font-bold hover:scale-105 transition-all flex items-center gap-2">
      <span class="material-symbols-outlined">notifications_active</span>
      Follow ${APP_MODE.x_handle} for launch
    </a>
  </div>
  `;
}

export function renderVaultDetail(vaultKey) {
  const vc = _getVault(vaultKey);
  if (!vc) return `<p class="text-error text-center py-12">Vault "${vaultKey}" not found.</p>`;
  // Visibility gate: even if an internal vault accidentally appears in
  // VAULT_CONFIGS (e.g. someone edits config.js by mistake), don't render
  // its detail page. The vault.registry visibility field is the source of
  // truth (commit c88b0e77), but config.js is hardcoded so we treat any
  // `visibility === "internal"` here as a defence-in-depth backstop.
  // Defaults to "public" via ?? so legacy entries without the field render.
  if ((vc.visibility ?? "public") === "internal") {
    return `<p class="text-error text-center py-12">Vault "${vaultKey}" not found.</p>`;
  }
  // Stealth default: render the muted view so non-depositors never see
  // metrics flash in. initVaultDetail swaps in the full live DOM if the
  // connected wallet is a depositor.
  if (isStealth() && vc.address && vc.status === "live") {
    return _renderStealthVaultDetail(vc);
  }
  if (!vc.address || vc.status !== "live") {
    return `
      <div class="text-center py-16">
        <div class="w-16 h-16 mx-auto rounded-2xl ${vc.color.bg20} flex items-center justify-center mb-4">
          <span class="material-symbols-outlined ${vc.color.text} text-3xl">${vc.icon}</span>
        </div>
        <h2 class="font-headline font-bold text-2xl text-on-surface mb-2">${vc.name}</h2>
        <p class="text-secondary/60 mb-6">${vc.subtitle} — coming soon.</p>
        <a href="#vaults" class="inline-flex items-center gap-2 px-4 py-2 bg-surface-container-high hover:bg-outline-variant/20 border border-outline-variant/20 rounded-lg text-on-surface font-headline text-sm transition-all">
          <span class="material-symbols-outlined text-sm">arrow_back</span> Back to Vaults
        </a>
      </div>`;
  }
  return _renderVaultDetailLive(vc);
}

function _renderVaultDetailLive(vc) {
  const m = vc.metrics || {};

  return `
  <!-- Vault Header -->
  <div class="flex items-center gap-4 mb-6">
    <a href="#vaults" class="w-8 h-8 flex items-center justify-center rounded-lg bg-surface-container-high hover:bg-outline-variant/20 transition-colors">
      <span class="material-symbols-outlined text-secondary text-sm">arrow_back</span>
    </a>
    <div class="w-12 h-12 rounded-lg ${vc.color.bg20} flex items-center justify-center ${vc.color.shadow}">
      <span class="material-symbols-outlined ${vc.color.text} text-2xl">${vc.icon}</span>
    </div>
    <div>
      <h2 class="font-headline font-bold text-2xl text-on-surface">${vc.name}</h2>
      <p class="text-sm text-secondary/60">${vc.subtitle}</p>
    </div>
    <div class="ml-auto flex items-center gap-2">
      <div class="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 rounded text-xs text-emerald-400">
        <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot"></span>Live
      </div>
      <a href="${EXPLORER}/address/${vc.address}" target="_blank"
         title="HyperEVM contract — deposits, withdrawals, share transfers, contract upgrades"
         class="px-2 py-1 bg-surface-container-high rounded text-xs text-secondary/40 hover:text-primary transition-colors flex items-center gap-1">
        Contract <span class="material-symbols-outlined text-xs">open_in_new</span>
      </a>
      <a href="${HL_EXPLORER}/address/${vc.address}" target="_blank"
         title="Hyperliquid Core — live trades, fills, positions, funding. This is where the bot actually executes trades against your USDC."
         class="px-2 py-1 bg-surface-container-high rounded text-xs text-secondary/40 hover:text-primary transition-colors flex items-center gap-1">
        Live trades <span class="material-symbols-outlined text-xs">open_in_new</span>
      </a>
    </div>
  </div>

  <!-- Stats — 5 tiles, one row at md+. Win Rate and Reward:Risk were
       removed at JP's call: they made the grid wrap to two rows, and
       early-life live numbers (n<30 closed trades per vault) aren't
       stable enough to be load-bearing on a public page. Net P&L
       since launch shows up via the Share Price tile (>$1 = profit). -->
  <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
    <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-4 glow relative">
      <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50">Share Price</p>
      <p class="text-xl font-headline font-bold ${vc.color.text}" id="vd-share-price">$1.00</p>
      <a href="https://crystalclear.finance/#how-it-works" target="_blank"
         title="What is a share? How does the price work? Who controls it? Where to verify on-chain?"
         class="text-[0.55rem] text-primary/60 hover:text-primary inline-flex items-center gap-0.5 mt-1 transition-colors">
        How does this work? <span class="material-symbols-outlined" style="font-size:10px;line-height:1">open_in_new</span>
      </a>
    </div>
    <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-4" title="Total Value Locked — total USDC deposited across all users in this vault.">
      <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50">TVL</p>
      <p class="text-xl font-headline font-bold text-on-surface tabular-nums" id="vd-tvl">$0</p>
    </div>
    <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-4" title="Sharpe ratio — risk-adjusted return. Higher = more reward per unit of volatility. Above 1 is good; above 2 is excellent.">
      <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50">Sharpe</p>
      <p class="text-xl font-headline font-bold text-on-surface tabular-nums">${m.sharpe?.toFixed(2) || '—'}</p>
    </div>
    <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-4" title="Max Drawdown (live since launch) — worst peak-to-trough share-price decline observed since this vault went live. Backtest MDDs are lower; live experience reflects the live execution environment.">
      <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50">Max DD</p>
      <p class="text-xl font-headline font-bold text-on-surface tabular-nums">${m.maxDD ? m.maxDD.toFixed(1) + '%' : '—'}</p>
    </div>
    <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-4" title="Avg holding time (live since launch) — median duration a position stays open, measured across all closed trades. Short hold time = macro factors don't have time to dominate any single trade outcome.">
      <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50">Avg Hold</p>
      <p class="text-xl font-headline font-bold text-on-surface tabular-nums">${m.medianHoldHours != null ? '~' + m.medianHoldHours.toFixed(1) + 'h' : '—'}</p>
    </div>
  </div>

  <!-- Share Price Chart -->
  <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-6 mb-6">
    <div class="flex justify-between items-center mb-4">
      <h3 class="font-headline font-bold text-lg text-on-surface flex items-center gap-2">
        <span class="material-symbols-outlined text-primary">show_chart</span>
        Share Price
      </h3>
      <div class="flex gap-1" id="vd-chart-pills">
        <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/20 text-on-surface-variant hover:text-primary" data-vd-tf="perpDay">1D</button>
        <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/20 text-on-surface-variant hover:text-primary" data-vd-tf="perpWeek">7D</button>
        <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-primary text-on-primary" data-vd-tf="perpMonth">1M</button>
        <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/20 text-on-surface-variant hover:text-primary" data-vd-tf="perpAllTime">All</button>
      </div>
    </div>
    <div class="h-72 relative" id="vd-chart-container"></div>
  </div>

  <!-- Deposit / Withdraw -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
    <!-- Deposit -->
    <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-6">
      <h3 class="font-headline font-bold text-on-surface mb-4 flex items-center gap-2">
        <span class="material-symbols-outlined ${vc.color.text}">arrow_downward</span>
        Deposit USDC
      </h3>
      <div class="mb-3">
        <div class="flex justify-between text-xs text-secondary/60 mb-1">
          <span>Amount</span>
          <span>Balance: <span id="vd-usdc-bal" class="text-primary">0.00</span> USDC</span>
        </div>
        <div class="flex items-center bg-surface-container-high rounded-lg border border-outline-variant/20 overflow-hidden">
          <input type="number" id="vd-dep-amount" placeholder="0.00" class="flex-1 bg-transparent px-4 py-3 text-lg font-headline text-on-surface outline-none" />
          <button id="vd-dep-max" class="px-3 py-1 mr-2 text-xs text-primary border border-primary/30 rounded hover:bg-primary/10">MAX</button>
          <span class="pr-4 text-sm text-secondary">USDC</span>
        </div>
      </div>
      <p class="text-xs text-secondary/60 mb-3">You receive: <span id="vd-dep-preview" class="text-on-surface font-semibold">0.0000</span> ${vc.symbol}</p>
      <div id="vd-dep-tip" class="hidden text-[0.65rem] mb-2 text-secondary/70 leading-snug bg-surface-container-high/40 border border-outline-variant/20 rounded-lg p-2"></div>
      <!-- Zap entry (shared with /deposit via deposit-ui.js — keeps both in sync) -->
      ${zapEntryHtml('vd-zap-btn')}
      <button id="vd-dep-btn" class="w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all duration-300 active:scale-[0.98]">
        Deposit
      </button>
      <p class="mt-2 text-[0.6rem] text-secondary/50 leading-snug">
        Deposits are locked from withdrawal for 1 hour (anti-MEV).
      </p>
      <!-- Activation-fee notice: shown pre-activation -->
      <div id="vd-activation-notice" class="hidden mt-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
        <p class="text-[0.65rem] text-amber-300/90 leading-snug">
          <span class="material-symbols-outlined text-xs align-middle">info</span>
          <strong>First depositor pays a one-time 1 USDC activation fee</strong> required by Hyperliquid to create this vault's Core account. After that, all future depositors (including you) deposit fee-free.
        </p>
      </div>
      <!-- Post-activation note: persistent, explains any one-time $1 gap for the first depositor -->
      <p id="vd-fees-note" class="hidden mt-2 text-[0.6rem] text-secondary/40 leading-snug">
        <span class="material-symbols-outlined text-[0.7rem] align-middle">info</span>
        Fees: <span id="vd-fee-perf">—</span> performance on profits above your cost basis, <span id="vd-fee-builder">—</span> builder fee on turnover.
        No deposit fee.
      </p>
      <!-- Bridging-in-progress indicator: shown while settlement is pending -->
      <div id="vd-bridging-notice" class="hidden mt-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
        <p class="text-[0.65rem] text-primary/90 leading-snug flex items-start gap-2">
          <span class="inline-block w-3 h-3 rounded-full border-2 border-primary/40 border-t-primary animate-spin mt-0.5 shrink-0"></span>
          <span>
            <strong>Bridging to Hyperliquid Core...</strong><br>
            Your deposit has been recorded. USDC is bridging to Core perps (~30-60s). TVL display updates once settlement completes.
          </span>
        </p>
      </div>
    </div>

    <!-- Withdraw -->
    <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-6">
      <h3 class="font-headline font-bold text-on-surface mb-4 flex items-center gap-2">
        <span class="material-symbols-outlined ${vc.color.text}">arrow_upward</span>
        Withdraw
      </h3>
      <div class="mb-3">
        <div class="flex justify-between text-xs text-secondary/60 mb-1">
          <span>Shares</span>
          <span>Balance: <span id="vd-shares-bal" class="text-primary">0.0000</span></span>
        </div>
        <div class="flex items-center bg-surface-container-high rounded-lg border border-outline-variant/20 overflow-hidden">
          <input type="number" id="vd-wd-shares" placeholder="0.00" class="flex-1 bg-transparent px-4 py-3 text-lg font-headline text-on-surface outline-none" />
          <button id="vd-wd-max" class="px-3 py-1 mr-2 text-xs text-primary border border-primary/30 rounded hover:bg-primary/10">MAX</button>
          <span class="pr-4 text-sm text-secondary">${vc.symbol}</span>
        </div>
      </div>
      <p class="text-xs text-secondary/60 mb-1 flex items-center gap-1">
        You receive: <span id="vd-wd-preview" class="text-on-surface font-semibold">0.00</span> USDC
        <span id="vd-wd-info" class="material-symbols-outlined text-secondary/40 text-sm cursor-help hidden" title="">info</span>
      </p>
      <p id="vd-wd-perf-note" class="hidden text-[0.6rem] text-secondary/50 leading-snug mb-2"></p>
      <div class="mb-3"></div>
      <button id="vd-wd-btn" class="w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all duration-300 active:scale-[0.98]">
        Request Withdrawal
      </button>
      <p class="mt-2 text-[0.6rem] text-secondary/50 leading-snug">
        2-step: Request now (signs once) → bot bridges funds back from perps (~2-15 min) → Claim (signs once). Most withdrawals served instantly from idle buffer.
      </p>

      <div id="vd-claim-box" class="hidden mt-4 p-4 rounded-lg border border-tertiary/20 bg-tertiary/5">
        <p class="text-sm text-tertiary font-semibold mb-1">Pending Withdrawal</p>
        <p class="text-xs text-secondary/80 mb-2 flex items-center gap-1">
          <span id="vd-claim-amount">0</span> USDC ready to claim
          <span id="vd-claim-info" class="material-symbols-outlined text-secondary/40 text-sm cursor-help hidden" title="">info</span>
        </p>
        <button id="vd-claim-btn" class="w-full py-2 bg-tertiary/20 hover:bg-tertiary/30 border border-tertiary/30 rounded-lg text-tertiary font-headline font-semibold text-sm">Claim USDC</button>
      </div>
    </div>
  </div>

  <!-- About this vault — describes what makes THIS vault different from
       the others in the fleet. Strategy count + symbol are populated
       dynamically by _refreshVaultDetail(); description is static and
       refers to selection criteria (which DON'T change with rebalances).
       Quick-facts pills clarify the bidirectional nature and that the
       timeframe label is candle TFs, not hold time.
       Rebalance history pulled from REBALANCE_HISTORY in config.js —
       update on each rebalance event. -->
  <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-6 mb-6">
    <h3 class="font-headline font-bold text-on-surface mb-3">About this vault</h3>
    <p class="text-sm text-secondary/80 mb-4 leading-relaxed">${vc.description}</p>

    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
      <div class="px-3 py-2 rounded-lg bg-surface-container-high" title="Number of crypto assets this vault trades. Each is traded both long and short.">
        <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50 mb-0.5">Universe</p>
        <p class="text-sm text-on-surface font-medium tabular-nums">${m.coins || '?'} coins · long/short</p>
      </div>
      <div class="px-3 py-2 rounded-lg bg-surface-container-high" title="Candle intervals the strategies make decisions on — NOT hold time. Median hold across the fleet is ~9 hours.">
        <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50 mb-0.5">Decision candles</p>
        <p class="text-sm text-on-surface font-medium tabular-nums">${m.timeframes || '?'}</p>
      </div>
      <div class="px-3 py-2 rounded-lg bg-surface-container-high" title="Number of strategies currently running in this vault. Updated on each rebalance — see history below.">
        <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50 mb-0.5">Active strategies</p>
        <p class="text-sm text-on-surface font-medium tabular-nums" id="vd-active-strategies">${
          REBALANCE_HISTORY[vc.key]?.[0]?.n_strategies ?? '—'
        }</p>
      </div>
      <div class="px-3 py-2 rounded-lg bg-surface-container-high" title="Backtested risk-adjusted return. Higher = more reward per unit of volatility. Live history will catch up over time.">
        <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50 mb-0.5">Backtest Sharpe</p>
        <p class="text-sm text-on-surface font-medium tabular-nums">${vc.metrics?.sharpe ? vc.metrics.sharpe.toFixed(2) : '—'}</p>
      </div>
      <div class="px-3 py-2 rounded-lg bg-surface-container-high" title="Performance fee — charged only on profits at withdrawal. No management fee, no subscription, no hidden costs.">
        <p class="text-[0.55rem] uppercase tracking-widest text-secondary/50 mb-0.5">Perf fee</p>
        <p class="text-sm text-on-surface font-medium tabular-nums" id="vd-about-perf">—</p>
      </div>
    </div>

    <p class="text-[0.65rem] text-on-surface-variant/60 leading-relaxed">
      Strategies are short-horizon: median position closes in ~9 hours across the fleet. The vault trades both long and short, covers both trending and choppy market regimes, and holds no passive crypto.
    </p>
  </div>

  `;
}

/**
 * Refresh DOM state from on-chain reads. Safe to call repeatedly — does NOT
 * (re-)attach event listeners. Use this after a successful tx instead of
 * re-running initVaultDetail (which would double-bind click handlers and
 * cause one user click to fire N MetaMask popups).
 */
async function _refreshVaultDetail(vaultKey) {
  const vc = _getVault(vaultKey);
  if (!vc || !vc.address || vc.status !== "live") return;
  const addr = getAddress();
  const rp = getReadProvider();
  const vault = new ethers.Contract(vc.address, VAULT_ABI, rp);
  try {
    const [totalAssets, totalSupply] = await Promise.all([vault.totalAssets(), vault.totalSupply()]);
    const tvl = Number(totalAssets) / 10 ** USDC_DECIMALS;
    const tvlEl = document.getElementById("vd-tvl");
    if (tvlEl) tvlEl.textContent = "$" + tvl.toFixed(0);

    let sharePriceText = "$1.0000";
    if (totalSupply > 0n && totalAssets === 0n) sharePriceText = "—";
    else if (totalSupply > 0n) {
      const assetsPerShare = await vault.convertToAssets(10n ** BigInt(SHARE_DECIMALS));
      const rawPrice = Number(assetsPerShare) / 10 ** USDC_DECIMALS;
      // Rebase: displayed = on_chain / launchSharePrice so UI shows $1.00 at launch
      // regardless of the one-time HL activation fee absorbed by the first depositor.
      const baseline = vc.launchSharePrice || 1.0;
      sharePriceText = "$" + (rawPrice / baseline).toFixed(4);
    }
    const spEl = document.getElementById("vd-share-price");
    if (spEl) spEl.textContent = sharePriceText;

    try {
      const activated = await vault.coreAccountActivated();
      const notice = document.getElementById("vd-activation-notice");
      const feesNote = document.getElementById("vd-fees-note");
      if (!activated) { notice?.classList.remove("hidden"); feesNote?.classList.add("hidden"); }
      else { notice?.classList.add("hidden"); feesNote?.classList.remove("hidden"); }
    } catch {}

    const bridging = document.getElementById("vd-bridging-notice");
    if (bridging) {
      if (totalSupply > 0n && totalAssets === 0n) bridging.classList.remove("hidden");
      else bridging.classList.add("hidden");
    }
  } catch (e) { console.error(e); }

  if (!addr) return;
  try {
    const [shares, , withdrawReq, perfFeeBps] = await Promise.all([
      vault.balanceOf(addr), vault.userCostBasis(addr), vault.withdrawalRequests(addr), vault.performanceFeeBps(),
    ]);
    const usdcBal = await getUsdcBalance();
    const usdcEl = document.getElementById("vd-usdc-bal");
    if (usdcEl) usdcEl.textContent = usdcBal.toFixed(2);
    // Refresh the help tip's DOM only (no listeners here — the zap button is
    // wired ONCE in initVaultDetail; this function must not bind, see docstring).
    try {
      const hypeBal = await getHypeBalance();
      const tipEl = document.getElementById("vd-dep-tip");
      if (tipEl) {
        const tip = usdcHelpTip({ noUsdc: usdcBal <= 0, lowHype: hypeBal < 0.005 });
        tipEl.innerHTML = tip || "";
        tipEl.classList.toggle("hidden", !tip);
      }
    } catch { /* tip is best-effort */ }
    // Stash raw BigInt for the MAX button (see _withdraw bug 2026-05-19 —
    // toFixed(4) round-trips can overshoot balanceOf, causing requestWithdraw
    // to revert. MAX uses dataset.raw, not the displayed text.)
    const sharesEl = document.getElementById("vd-shares-bal");
    if (sharesEl) {
      sharesEl.textContent = (Number(shares) / 10 ** SHARE_DECIMALS).toFixed(4);
      sharesEl.dataset.raw = shares.toString();
    }

    const pendingShares = withdrawReq[0];
    const claimBox = document.getElementById("vd-claim-box");
    if (pendingShares > 0n) {
      const costBasisSnap = withdrawReq[1];
      const totalSharesSnap = withdrawReq[2];
      // V3.6 LOCKS the gross-asset payout at request time via snapshottedAssets
      // (withdrawReq[4]). Use it directly — that's exactly what claimWithdraw
      // pays. The (shares * totalAssets / totalSupply) recomputation against
      // CURRENT chain state was the V3.4 path and produces wrong numbers as
      // soon as totalAssets drifts post-request (Diamond user 2026-05-19 saw
      // $10.26 in UI while contract paid $24.02 — 57% under-report).
      // snapshottedAssets == 0 means a legacy pre-V3.6 pending request still
      // exists; fall back to the recomputation only for those.
      const snapshottedAssets = withdrawReq[4];
      let grossAssets;
      if (snapshottedAssets > 0n) {
        grossAssets = snapshottedAssets;
      } else {
        const [ta, ts] = await Promise.all([vault.totalAssets(), vault.totalSupply()]);
        grossAssets = (pendingShares * (ta + 1n)) / (ts + pendingShares + 1000n);
      }
      const fee = computeWithdrawalFee(pendingShares, totalSharesSnap, costBasisSnap, grossAssets, perfFeeBps);
      const net = grossAssets - fee;
      claimBox?.classList.remove("hidden");
      const ca = document.getElementById("vd-claim-amount");
      if (ca) ca.textContent = (Number(net) / 10 ** USDC_DECIMALS).toFixed(2);
      const infoIcon = document.getElementById("vd-claim-info");
      const locked = snapshottedAssets > 0n;
      const tail = locked
        ? "Amount is locked: this is exactly what the contract will pay on claim."
        : "Amount fluctuates with vault NAV until claimed.";  // legacy pre-V3.6 path
      if (fee > 0n && infoIcon) {
        const grossH = (Number(grossAssets) / 10 ** USDC_DECIMALS).toFixed(4);
        const feeH = (Number(fee) / 10 ** USDC_DECIMALS).toFixed(4);
        const netH = (Number(net) / 10 ** USDC_DECIMALS).toFixed(4);
        infoIcon.setAttribute("title", `Gross $${grossH} − perf fee $${feeH} = net $${netH}. ${tail}`);
        infoIcon.classList.remove("hidden");
      } else if (infoIcon) {
        infoIcon.setAttribute("title", tail);
        infoIcon.classList.remove("hidden");
      }
    } else {
      claimBox?.classList.add("hidden");
    }
  } catch (e) { console.error(e); }
}

// ── Share Price Chart (HL portfolio history, scaled to on-chain truth) ───

let _vdChart = null;
let _vdPortfolio = null;
let _vdOnChainHistory = null;  // Array of {ts, ta, ts_} from snapshot service
let _vdOnChainSharePrice = null;  // Set by initVaultDetail before chart builds
let _vdLaunchSharePrice = 1.0;   // Rebase baseline (hides activation fee)

// Try to load on-chain share price history from the API.
//
// Pre-2026-05-11 this fetched static JSONL files from /data/<key>_share_price_<ym>.jsonl
// served by Lightsail nginx (which had local file access to the runner's
// outputs). After the serverless migration that path 403s because the
// frontend S3 bucket doesn't carry those files (they live in
// crystalclear-vault-data via the sidecar). Users with browser-cached copies
// of the OLD JSONL response then saw their chart frozen at the May-11 cutoff
// indefinitely.
//
// Fix: route through /api/vault/<key>/share-price-history (Lambda reads
// vault-data bucket, returns fresh data). Same dispatch pattern used by
// performance.js since 2026-05-11 evening.
async function _fetchOnChainHistory(vaultKey) {
  if (!vaultKey) return null;
  // Match the local-only window we used to fetch: current + previous month
  // of points. The endpoint accepts `days` (1-180). 45 covers two months
  // plus headroom for binary-search tolerances inside the chart's filter.
  const apiBase = (typeof window !== "undefined" && typeof window.CC_API_BASE === "string")
    ? window.CC_API_BASE
    : "";
  const url = `${apiBase}/api/vault/${vaultKey.toLowerCase()}/share-price-history?days=45`;
  try {
    const r = await fetch(url, { cache: "no-cache" });
    if (!r.ok) return null;
    const payload = await r.json();
    const points = payload?.points || [];
    if (!points.length) return null;
    // Backend already sorts ascending and dedupes; trust the response.
    // Keep the raw {ts, ta, ts_} shape — _computeOnChainPoints parses BigInt
    // strings from these fields.
    return points;
  } catch {
    return null;
  }
}

async function _buildVaultChart(vaultAddress, launchSharePrice, vaultKey) {
  // Wrap the whole flow so a thrown error inside any await doesn't become a
  // silent unhandled rejection. Surfaces in console + lets us still bind tab
  // handlers below even if the initial render trips.
  try {
    _vdLaunchSharePrice = launchSharePrice || 1.0;
    _vdOnChainHistory = await _fetchOnChainHistory(vaultKey);

    try {
      const apiBase = typeof window.CC_API_BASE === 'string' ? window.CC_API_BASE : '';
      const resp = await fetch(`${apiBase}/api/hl/info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "portfolio", user: vaultAddress }),
      });
      const raw = await resp.json();
      _vdPortfolio = {};
      for (const [key, val] of raw) _vdPortfolio[key] = val;
    } catch (e) {
      console.warn("[vd-chart] HL portfolio fetch failed:", e);
      if (!_vdOnChainHistory) return;
    }

    _renderVdChart("perpMonth");
  } catch (e) {
    console.error("[vd-chart] _buildVaultChart failed:", e);
  }

  // Bind tab handlers regardless — clicks can re-trigger render if needed.
  document.querySelectorAll("[data-vd-tf]").forEach(btn => {
    btn.addEventListener("click", () => {
      document.querySelectorAll("[data-vd-tf]").forEach(b => {
        b.classList.remove("bg-primary", "text-on-primary");
        b.classList.add("bg-surface-container-lowest", "ring-1", "ring-outline-variant/20",
                        "text-on-surface-variant");
      });
      btn.classList.add("bg-primary", "text-on-primary");
      btn.classList.remove("bg-surface-container-lowest", "ring-1", "ring-outline-variant/20",
                           "text-on-surface-variant");
      _renderVdChart(btn.dataset.vdTf);
    });
  });
}

// Compute chart points from on-chain snapshot history (ground truth).
// Returns null if history is empty or filtered window too sparse.
//
// Plots share price NORMALIZED TO LAUNCH (rawPrice / launchSharePrice) — same
// formula as the "Share Price" KPI at the top of the page. Every vault visibly
// starts at $1.00 (the launch baseline) and the rightmost point matches the KPI
// (current launch-normalized share price).
//
// Caveat for Onyx specifically: launchSharePrice=$0.90 AND the snapshot logger
// only started writing 7 days post-launch. Without intervention, the leftmost
// chart point would land at ~$1.02 (= first observed raw $0.918 / launch $0.90)
// — not $1.00 as the user expects. We prepend a synthetic launch point at
// y=$1.00 so every vault chart starts at $1.00 visually. For all post-Onyx vaults
// (launchSharePrice=$1.00 + logger active from deploy) this is a no-op since
// the first real snapshot already lands at $1.00.
//
// Original bug fixed 2026-04-30: chart used to rebase to first-observed-point
// = $1.00, which made the chart and KPI disagree at the current point (chart
// said $1.02, KPI said $0.98). Caught by JP on Ruby.
function _computeOnChainPoints(tfKey) {
  if (!_vdOnChainHistory || _vdOnChainHistory.length < 2) return null;
  const windowMs = {
    perpDay: 24 * 3600 * 1000,
    perpWeek: 7 * 24 * 3600 * 1000,
    perpMonth: 30 * 24 * 3600 * 1000,
    perpAllTime: Infinity,
  }[tfKey] || 7 * 24 * 3600 * 1000;
  const cutoff = Date.now() - windowMs;
  const filtered = _vdOnChainHistory.filter(p => p.ts >= cutoff);
  if (filtered.length < 2) return null;
  const USDC_DECIMALS = 6, SHARE_DECIMALS = 9;
  const launchBaseline = _vdLaunchSharePrice || 1.0;
  const points = filtered.map(p => {
    const ta = Number(BigInt(p.ta)) / 10 ** USDC_DECIMALS;
    const ts = Number(BigInt(p.ts_)) / 10 ** SHARE_DECIMALS;
    const rawPrice = ts > 0 ? ta / ts : 1;
    return { x: p.ts, y: rawPrice / launchBaseline };
  });
  // Synthesize a launch point at y=1.00 if the first observation drifted
  // (>0.5% off $1.00). Only triggers for Onyx today (logger started 7d late).
  // For all vaults with launchSharePrice=1.00 deployed since 2026-04-22, the
  // first real snapshot already lands at ~$1.00 so this is a no-op.
  if (Math.abs(points[0].y - 1.0) > 0.005) {
    points.unshift({ x: points[0].x - 60_000, y: 1.0 });
  }
  return points;
}

// Compute chart points from HL portfolio (fallback — scaled to on-chain truth).
function _computeHLPoints(tfKey) {
  if (!_vdPortfolio) return null;
  const tfData = _vdPortfolio[tfKey];
  if (!tfData || !tfData.accountValueHistory || tfData.accountValueHistory.length < 2) {
    return null;
  }
  const avHist = tfData.accountValueHistory;
  const pnlHist = tfData.pnlHistory || [];
  const rawPoints = [];
  for (let i = 0; i < avHist.length; i++) {
    const [ts, avStr] = avHist[i];
    const pnlStr = pnlHist[i] ? pnlHist[i][1] : "0";
    const av = parseFloat(avStr);
    const pnl = parseFloat(pnlStr);
    const deposits = av - pnl;
    if (deposits < 0.5) continue;
    rawPoints.push({ x: ts, y: av / deposits });
  }
  if (!rawPoints.length) return null;
  // Scale so last point matches current on-chain share price
  if (_vdOnChainSharePrice) {
    const lastHL = rawPoints[rawPoints.length - 1].y;
    if (lastHL > 0) {
      const scale = _vdOnChainSharePrice / lastHL;
      return rawPoints.map(p => ({ x: p.x, y: p.y * scale }));
    }
  }
  return rawPoints;
}

function _renderVdChart(tfKey) {
  // ECharts replaces Chart.js for this chart. Chart.js v4 + MetaMask SES
  // lockdown reliably throws inside bindUserEvents when the chart is built
  // during the page's init storm. ECharts (already used on the internal
  // dashboard) doesn't have the same interaction. Same data, same look.
  const container = document.getElementById("vd-chart-container");
  if (!container) { console.warn("[vd-chart] no container"); return; }
  if (typeof echarts === "undefined") {
    console.warn("[vd-chart] ECharts not loaded");
    return;
  }

  // On-chain snapshots only (totalAssets/totalSupply at fixed intervals).
  // We previously tried backfilling Apr 13 → Apr 20 from HL portfolio
  // (av/(av-pnl)) but that formula spikes whenever a deposit or withdraw
  // happens, producing a misleading $1.20 peak around Apr 17. Real share
  // price is smooth across deposits/withdrawals — only changes on PnL.
  // Honest UX: show what we have, label when snapshots began.
  let points = _computeOnChainPoints(tfKey);
  if (!points) {
    points = _computeHLPoints(tfKey);  // last-resort fallback when no on-chain data
  }

  if (!points || points.length < 2) {
    if (_vdChart) { try { _vdChart.dispose(); } catch {} _vdChart = null; }
    container.innerHTML =
      '<p class="text-secondary/40 text-sm absolute inset-0 flex items-center justify-center">Not enough data for this period</p>';
    return;
  }

  // ECharts wants a div container without other children. The template still
  // ships a stale <canvas id="vd-chart"> from the Chart.js era — nuke it
  // before init so ECharts owns the container's children.
  if (!_vdChart || !echarts.getInstanceByDom(container)) {
    container.innerHTML = "";
    _vdChart = echarts.init(container, null, { renderer: "canvas" });
    window.addEventListener("resize", () => { if (_vdChart) _vdChart.resize(); });
  }

  const data = points.map(p => [p.x, p.y]);
  _vdChart.setOption({
    backgroundColor: "transparent",
    textStyle: { color: "#89ceff", fontFamily: "Inter, sans-serif" },
    grid: { left: 60, right: 20, top: 16, bottom: 30, containLabel: true },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#1a1f2d",
      borderColor: "#3c494c",
      borderWidth: 1,
      textStyle: { color: "#dee2f5", fontFamily: "Inter, sans-serif", fontSize: 12 },
      formatter: (params) => {
        const p = params[0];
        const d = new Date(p.value[0]);
        const ts = d.toLocaleString("en-GB", {
          timeZone: tzZone(), month: "short", day: "numeric",
          hour: "2-digit", minute: "2-digit",
        }) + " " + tzLabel(d);
        return `<div style="font-family:'Space Grotesk'">${ts}</div><div>Share Price: <b>$${p.value[1].toFixed(4)}</b></div>`;
      },
      axisPointer: { type: "line", lineStyle: { color: "#3c494c" } },
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: "#3c494c" } },
      axisLabel: { color: "#89ceff80", fontSize: 10 },
      splitLine: { lineStyle: { color: "#3c494c15" } },
    },
    yAxis: {
      type: "value",
      scale: true,
      axisLine: { lineStyle: { color: "#3c494c" } },
      axisLabel: {
        color: "#89ceff80", fontSize: 10,
        formatter: (v) => "$" + Number(v).toFixed(4),
      },
      splitLine: { lineStyle: { color: "#3c494c15" } },
    },
    series: [{
      type: "line",
      smooth: false,
      symbol: "none",
      sampling: "lttb",  // visual downsampling for large series
      lineStyle: { color: "#22d3ee", width: 2 },
      areaStyle: {
        color: {
          type: "linear", x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: "rgba(34,211,238,0.15)" },
            { offset: 1, color: "rgba(34,211,238,0)" },
          ],
        },
      },
      data,
    }],
  }, true /* not-merge: replace fully on tab switch */);
}

export async function initVaultDetail(vaultKey) {
  const vc = _getVault(vaultKey);
  if (!vc || !vc.address || vc.status !== "live") return;

  // Stealth gate: non-depositors see the muted countdown view. Depositors get
  // the full dashboard with deposit CTA locked. Whitelisted testers also get
  // the full dashboard but with the deposit CTA ENABLED so they can deposit
  // pre-launch. New deposits stay closed for everyone else in stealth.
  if (isStealth()) {
    const addrS = getAddress();
    const onWhitelist = canBypassStealth(addrS);
    if (addrS && (onWhitelist || await isDepositor(addrS))) {
      document.getElementById("page-content").innerHTML = _renderVaultDetailLive(vc);
      await _initVaultDetailLive(vaultKey, vc);
      if (!onWhitelist) _lockVaultDetailDepositCTA();
      return;
    }
    startCountdown(document.getElementById("vd-stealth-countdown"));
    return;
  }

  await _initVaultDetailLive(vaultKey, vc);
  // Legacy→Lagoon: founding-fleet vault closed to new deposits. Disable the
  // deposit form + explain; withdraw section is left untouched.
  if (vc.deposit_closed === true) _lockVaultDetailDepositClosed(vc);
}

// Disable the deposit form for a vault closed to NEW deposits (withdraw stays
// fully functional). Mirrors the stealth lock but with transition copy + a
// link to the live track record.
function _lockVaultDetailDepositClosed(vc) {
  const btn = document.getElementById("vd-dep-btn");
  if (btn) {
    // disabled=true suppresses the deposit + connect-wallet handlers without
    // handler surgery; withdraw section untouched.
    btn.disabled = true;
    btn.textContent = "Closed to new deposits";
    btn.classList.add("opacity-40", "cursor-not-allowed");
  }
  const amt = document.getElementById("vd-dep-amount");
  if (amt) { amt.disabled = true; amt.value = ""; amt.placeholder = "Deposits closed"; }
  const max = document.getElementById("vd-dep-max");
  if (max) max.disabled = true;
  const lock = document.getElementById("vd-fees-note");
  if (lock) {
    lock.classList.remove("hidden");
    lock.innerHTML = `<span class="material-symbols-outlined text-[0.7rem] align-middle">info</span> `
      + `Closed to new deposits during the Lagoon transition. Your existing position and `
      + `<span class="text-on-surface">withdrawals are unaffected</span> — the track record stays live &amp; verifiable.`;
  }
}

function _lockVaultDetailDepositCTA() {
  const btn = document.getElementById("vd-dep-btn");
  if (btn) {
    btn.disabled = true;
    btn.textContent = `Deposits open ${launchDateLabel()}`;
    btn.classList.add("opacity-40", "cursor-not-allowed");
  }
  // Input + MAX also dead so users don't type a number that goes nowhere
  const depAmount = document.getElementById("vd-dep-amount");
  if (depAmount) depAmount.disabled = true;
  const depMax = document.getElementById("vd-dep-max");
  if (depMax) depMax.disabled = true;
}

async function _initVaultDetailLive(vaultKey, vc) {
  const addr = getAddress();
  const rp = getReadProvider();
  const vault = new ethers.Contract(vc.address, VAULT_ABI, rp);

  // Load vault state
  try {
    const [totalAssets, totalSupply] = await Promise.all([vault.totalAssets(), vault.totalSupply()]);
    const tvl = Number(totalAssets) / 10 ** USDC_DECIMALS;
    document.getElementById("vd-tvl").textContent = "$" + tvl.toFixed(0);

    // Share price: only meaningful when both supply and assets are non-trivial.
    // Degenerate state (supply > 0 but assets == 0) would show a nonsense
    // figure like $100,000 due to the virtual-shares offset math. Display "—"
    // instead and rely on the bridging-in-progress banner to explain state.
    let sharePriceText = "$1.0000";
    _vdOnChainSharePrice = null;
    if (totalSupply > 0n && totalAssets === 0n) {
      sharePriceText = "—";
    } else if (totalSupply > 0n) {
      const assetsPerShare = await vault.convertToAssets(10n ** BigInt(SHARE_DECIMALS));
      const rawPrice = Number(assetsPerShare) / 10 ** USDC_DECIMALS;
      // Rebase: displayed = on_chain / launchSharePrice so UI shows $1.00 at launch
      // regardless of the one-time HL activation fee absorbed by the first depositor.
      const baseline = vc.launchSharePrice || 1.0;
      _vdOnChainSharePrice = rawPrice / baseline;
      sharePriceText = "$" + _vdOnChainSharePrice.toFixed(4);
    }
    document.getElementById("vd-share-price").textContent = sharePriceText;

    // Activation-fee disclosure: show only if coreAccountActivated is false.
    // (Swallow ABI-missing errors on older contracts.)
    try {
      const activated = await vault.coreAccountActivated();
      const notice = document.getElementById("vd-activation-notice");
      const feesNote = document.getElementById("vd-fees-note");
      if (!activated) {
        notice?.classList.remove("hidden");
        feesNote?.classList.add("hidden");
      } else {
        notice?.classList.add("hidden");
        feesNote?.classList.remove("hidden");
      }
    } catch {}

    // Bridging-in-progress indicator: when users have deposited (totalSupply>0)
    // but totalAssets is still reading 0, bridging hasn't settled yet (or stuck).
    const bridging = document.getElementById("vd-bridging-notice");
    if (bridging) {
      if (totalSupply > 0n && totalAssets === 0n) bridging.classList.remove("hidden");
      else bridging.classList.add("hidden");
    }
  } catch (e) { console.error(e); }

  // Build share price chart (HL portfolio history — runs in background)
  _buildVaultChart(vc.address, vc.launchSharePrice, vc.key);

  // Fee rates — read once from contract so we never hardcode values that can
  // drift from on-chain state (historical drift: UI said 15% / 0.04% while
  // chain was 20% / 0.035%).
  let perfFeeBps = 0n;
  try {
    perfFeeBps = await vault.performanceFeeBps();
    const perfPct = formatPerfPct(perfFeeBps);
    // 🔴 Builder fee: the on-chain builderFeeRate() is the CAP (100 = 0.1%), NOT
    // what we charge. The actual per-order rate is f=25 = 0.025% (runner
    // BUILDER_FEE, core/chain_adapters/hyperliquid.py) and isn't stored on-chain,
    // so display the REAL rate as a constant — reading the cap showed a wrong
    // 0.1% (4× too high). Keep perfFeeBps from-chain (that one IS correct on-chain).
    const builderPct = formatBuilderPct(25n);
    const perfEl = document.getElementById("vd-fee-perf"); if (perfEl) perfEl.textContent = perfPct;
    const builderEl = document.getElementById("vd-fee-builder"); if (builderEl) builderEl.textContent = builderPct;
    // The About-card "Perf fee" tile shows just the percentage — the
    // label is on the tile heading above the value.
    // Builder fee deliberately NOT surfaced on the per-vault page —
    // it's already netted into trade P&L and not a user-facing decision.
    const aboutPerf = document.getElementById("vd-about-perf"); if (aboutPerf) aboutPerf.textContent = perfPct;
  } catch (e) { console.error("fee rate load failed", e); }

  if (!addr) {
    // Disconnected: public state (TVL, share price, chart) already rendered
    // above. Flip action buttons to "Connect Wallet" so they're obviously
    // not dead — clicking routes to the top-nav Connect handler.
    const connect = () => document.getElementById("connect-btn")?.click();
    const depBtn = document.getElementById("vd-dep-btn");
    const wdBtn  = document.getElementById("vd-wd-btn");
    if (depBtn) { depBtn.textContent = "Connect Wallet"; depBtn.addEventListener("click", connect); }
    if (wdBtn)  { wdBtn.textContent  = "Connect Wallet"; wdBtn.addEventListener("click", connect); }
    wireZapEntry('vd-zap-btn'); // zap works disconnected too — it prompts connect
    return;
  }

  // Load user state (kept in closure for the input handlers — used to compute
  // the perf fee component of the withdraw preview: fee depends on this user's
  // cost basis, not just contract-level state).
  let userShares = 0n;
  let userCostBasis = 0n;
  try {
    const [shares, costBasis, withdrawReq] = await Promise.all([
      vault.balanceOf(addr), vault.userCostBasis(addr), vault.withdrawalRequests(addr),
    ]);
    userShares = shares;
    userCostBasis = costBasis;
    const usdcBal = await getUsdcBalance();
    document.getElementById("vd-usdc-bal").textContent = usdcBal.toFixed(2);
    // Cross-chain entry (wire ONCE here) + contextual help — shared with /deposit
    // via deposit-ui.js so the two deposit surfaces can't drift apart.
    wireZapEntry('vd-zap-btn');
    try {
      const hypeBal = await getHypeBalance();
      const tipEl = document.getElementById("vd-dep-tip");
      if (tipEl) {
        const tip = usdcHelpTip({ noUsdc: usdcBal <= 0, lowHype: hypeBal < 0.005 });
        tipEl.innerHTML = tip || "";
        tipEl.classList.toggle("hidden", !tip);
      }
    } catch { /* tip is best-effort */ }
    // Stash raw BigInt for MAX (mirrors _refreshVaultDetail above; bug 2026-05-19).
    const sharesBalEl = document.getElementById("vd-shares-bal");
    sharesBalEl.textContent = (Number(shares) / 10 ** SHARE_DECIMALS).toFixed(4);
    sharesBalEl.dataset.raw = shares.toString();

    const pendingShares = withdrawReq[0];
    const claimBox = document.getElementById("vd-claim-box");
    if (pendingShares > 0n) {
      const costBasisSnap = withdrawReq[1];
      const totalSharesSnap = withdrawReq[2];
      // V3.6: prefer the locked snapshottedAssets (withdrawReq[4]) — that's
      // exactly what claimWithdraw will pay. Fallback to the V3.4 live-recompute
      // only if snapshottedAssets == 0 (legacy pre-V3.6 pending request).
      // See c88b0e77's sibling commit + bug 2026-05-19 Diamond ($10.26 UI vs
      // $24.02 actual).
      const snapshottedAssets = withdrawReq[4];
      let grossAssets;
      if (snapshottedAssets > 0n) {
        grossAssets = snapshottedAssets;
      } else {
        const [ta, ts] = await Promise.all([vault.totalAssets(), vault.totalSupply()]);
        grossAssets = (pendingShares * (ta + 1n)) / (ts + pendingShares + 1000n);
      }
      const fee = computeWithdrawalFee(pendingShares, totalSharesSnap, costBasisSnap, grossAssets, perfFeeBps);
      const net = grossAssets - fee;
      claimBox.classList.remove("hidden");
      document.getElementById("vd-claim-amount").textContent = (Number(net) / 10 ** USDC_DECIMALS).toFixed(2);
      const feeLine = document.getElementById("vd-claim-fee-line");
      if (fee > 0n && feeLine) {
        document.getElementById("vd-claim-gross").textContent = (Number(grossAssets) / 10 ** USDC_DECIMALS).toFixed(2);
        document.getElementById("vd-claim-fee").textContent = (Number(fee) / 10 ** USDC_DECIMALS).toFixed(4);
        feeLine.classList.remove("hidden");
      } else if (feeLine) {
        feeLine.classList.add("hidden");
      }
    } else {
      claimBox?.classList.add("hidden");
    }
  } catch (e) { console.error(e); }

  // Wire up buttons
  const depInput = document.getElementById("vd-dep-amount");
  const wdInput = document.getElementById("vd-wd-shares");

  depInput.addEventListener("input", async () => {
    const amount = parseFloat(depInput.value) || 0;
    if (amount <= 0) { document.getElementById("vd-dep-preview").textContent = "0.0000"; return; }
    const shares = await vault.convertToShares(BigInt(Math.floor(amount * 10 ** USDC_DECIMALS)));
    document.getElementById("vd-dep-preview").textContent = (Number(shares) / 10 ** SHARE_DECIMALS).toFixed(4);
  });

  wdInput.addEventListener("input", async (e) => {
    // Trusted = real user typing → invalidate MAX's stashed raw value so
    // _withdraw uses the typed parseFloat instead. MAX dispatches its own
    // synthetic 'input' event (isTrusted=false) to refresh the preview
    // without nuking the maxRaw it just set. See bug 2026-05-19.
    if (e.isTrusted) delete wdInput.dataset.maxRaw;
    const shares = parseFloat(wdInput.value) || 0;
    const infoIcon = document.getElementById("vd-wd-info");
    const perfNote = document.getElementById("vd-wd-perf-note");
    if (shares <= 0) {
      document.getElementById("vd-wd-preview").textContent = "0.00";
      infoIcon?.classList.add("hidden");
      perfNote?.classList.add("hidden");
      return;
    }
    const sharesBn = BigInt(Math.floor(shares * 10 ** SHARE_DECIMALS));
    const grossAssets = await vault.convertToAssets(sharesBn);
    const fee = computeWithdrawalFee(sharesBn, userShares, userCostBasis, grossAssets, perfFeeBps);
    const net = grossAssets - fee;
    const grossH = Number(grossAssets) / 10 ** USDC_DECIMALS;
    const feeH = Number(fee) / 10 ** USDC_DECIMALS;
    const netH = Number(net) / 10 ** USDC_DECIMALS;
    document.getElementById("vd-wd-preview").textContent = netH.toFixed(2);
    // Proactive disclosure only when a fee actually applies (user has profit).
    // One-line nudge; full breakdown still reachable via the (i) tooltip.
    if (fee > 0n) {
      const pctDisplay = Number(perfFeeBps) / 100;
      const profitH = grossH - ((Number(userCostBasis) / 10 ** USDC_DECIMALS) * Number(sharesBn) / Number(userShares || 1n));
      if (perfNote) {
        perfNote.textContent = `Includes ${pctDisplay}% perf fee on $${Math.max(0, profitH).toFixed(4)} profit.`;
        perfNote.classList.remove("hidden");
      }
      if (infoIcon) {
        infoIcon.setAttribute("title", `Gross $${grossH.toFixed(4)} − perf fee $${feeH.toFixed(4)} = net $${netH.toFixed(4)}.`);
        infoIcon.classList.remove("hidden");
      }
    } else {
      infoIcon?.classList.add("hidden");
      perfNote?.classList.add("hidden");
    }
  });

  document.getElementById("vd-dep-max").addEventListener("click", () => {
    depInput.value = document.getElementById("vd-usdc-bal").textContent;
    depInput.dispatchEvent(new Event("input"));
  });

  // Withdraw MAX: prefer the stashed raw BigInt (set when balance is rendered)
  // over the .toFixed(4) display text. The text rounds half-up; re-parsing
  // it can overshoot balanceOf and cause requestWithdraw to revert. Stash
  // the raw on the INPUT so _withdraw can pick it up. Manual typing clears
  // it via the existing 'input' listener below. See bug 2026-05-19.
  document.getElementById("vd-wd-max").addEventListener("click", () => {
    const balEl = document.getElementById("vd-shares-bal");
    const raw = balEl.dataset.raw;
    if (raw && raw !== "0") {
      // Display full precision (not toFixed) so the user sees what's being
      // submitted. The raw value is the source of truth for the tx itself.
      wdInput.value = (Number(BigInt(raw)) / 10 ** SHARE_DECIMALS).toString();
      wdInput.dataset.maxRaw = raw;
    } else {
      wdInput.value = "0";
      delete wdInput.dataset.maxRaw;
    }
    wdInput.dispatchEvent(new Event("input"));
  });

  const depBtn = document.getElementById("vd-dep-btn");
  depBtn.addEventListener("click", async () => {
    if (depBtn.disabled) return;  // re-entrancy guard
    const amount = parseFloat(depInput.value);
    if (!amount || amount <= 0) return;

    // First-deposit T&C acknowledgement (per wallet address). Shows once
    // per address; choice stored in localStorage so the user doesn't see
    // it on every deposit. The legal text lives at /terms.html.
    const tcKey = `tc-accepted-${addr.toLowerCase()}`;
    if (!localStorage.getItem(tcKey)) {
      const accepted = await _showTermsAcknowledgement();
      if (!accepted) return;
      localStorage.setItem(tcKey, String(Date.now()));
    }

    const signer = getSigner();
    const vaultW = new ethers.Contract(vc.address, VAULT_ABI, signer);
    const usdc = new ethers.Contract(USDC_ADDRESS, ERC20_ABI, signer);
    const assets = BigInt(Math.floor(amount * 10 ** USDC_DECIMALS));
    depBtn.disabled = true;
    try {
      const allowance = await usdc.allowance(addr, vc.address);
      if (allowance < assets) {
        showTxModal("Approving USDC", "Please confirm in MetaMask...");
        const tx = await usdc.approve(vc.address, ethers.MaxUint256);
        await tx.wait();
      }
      showTxModal("Depositing " + amount.toFixed(2) + " USDC", "Please confirm in MetaMask...");
      const tx = await vaultW.deposit(assets, addr, { gasLimit: 500000 });
      const receipt = await tx.wait();
      txModalSuccess("Deposited " + amount.toFixed(2) + " USDC into " + vc.name, receipt.hash);
      depInput.value = "";
      _refreshVaultDetail(vaultKey);
    } catch (e) {
      txModalError(e.reason || e.shortMessage || "Deposit failed");
    } finally {
      depBtn.disabled = false;
    }
  });

  const wdBtn = document.getElementById("vd-wd-btn");
  wdBtn.addEventListener("click", async () => {
    if (wdBtn.disabled) return;
    // Prefer raw BigInt from MAX click (set on wdInput.dataset.maxRaw).
    // Re-parsing a toFixed(4) float can overshoot balanceOf and revert.
    // User typing clears maxRaw via the input listener above. See 2026-05-19.
    let sharesBn;
    if (wdInput.dataset.maxRaw) {
      sharesBn = BigInt(wdInput.dataset.maxRaw);
      if (sharesBn <= 0n) return;
    } else {
      const shares = parseFloat(wdInput.value);
      if (!shares || shares <= 0) return;
      sharesBn = BigInt(Math.floor(shares * 10 ** SHARE_DECIMALS));
    }
    const signer = getSigner();
    const vaultW = new ethers.Contract(vc.address, VAULT_ABI, signer);
    wdBtn.disabled = true;
    try {
      showTxModal("Requesting Withdrawal", "Please confirm in MetaMask...");
      const tx = await vaultW.requestWithdraw(sharesBn, { gasLimit: 300000 });
      const receipt = await tx.wait();
      txModalSuccess("Withdrawal requested from " + vc.name, receipt.hash);
      wdInput.value = "";
      delete wdInput.dataset.maxRaw;  // stale after tx
      _refreshVaultDetail(vaultKey);
    } catch (e) {
      // V3.3 deposit-lock: friendlier message when user tries to withdraw <1h after deposit
      const errMsg = e.reason || e.shortMessage || e.message || "";
      if (errMsg.includes("WithdrawalTooSoonAfterDeposit")) {
        txModalError("Recently deposited — withdrawals are locked for 1 hour after deposit (anti-MEV).");
        return;
      }
      txModalError(e.reason || e.shortMessage || "Withdrawal failed");
    } finally {
      wdBtn.disabled = false;
    }
  });

  const claimBtn = document.getElementById("vd-claim-btn");
  claimBtn?.addEventListener("click", async () => {
    if (claimBtn.disabled) return;
    const signer = getSigner();
    const vaultW = new ethers.Contract(vc.address, VAULT_ABI, signer);
    claimBtn.disabled = true;
    try {
      showTxModal("Claiming USDC", "Please confirm in MetaMask...");
      const tx = await vaultW.claimWithdraw({ gasLimit: 200000 });
      const receipt = await tx.wait();
      txModalSuccess("USDC claimed from " + vc.name, receipt.hash);
      _refreshVaultDetail(vaultKey);
    } catch (e) {
      txModalError(e.reason || e.shortMessage || "Claim failed");
    } finally {
      claimBtn.disabled = false;
    }
  });
}


// First-deposit Terms of Service click-through. Resolves true if the user
// checks the box and clicks Accept, false otherwise. Blocks the deposit flow
// until resolved. Choice persists in localStorage per-wallet so the user
// only sees it once.
function _showTermsAcknowledgement() {
  return new Promise((resolve) => {
    // Build modal DOM
    const overlay = document.createElement("div");
    overlay.className = "fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4";
    overlay.innerHTML = `
      <div class="bg-surface-container border border-outline-variant/20 rounded-2xl max-w-lg w-full p-6">
        <h3 class="font-headline text-xl text-on-surface mb-4">Before your first deposit</h3>
        <div class="text-sm text-secondary/80 space-y-3 max-h-64 overflow-y-auto pr-2">
          <p><strong class="text-on-surface">This vault trades leveraged perpetuals.</strong>
            You may lose some or all of your deposit. Past performance does not guarantee future returns.</p>
          <p><strong class="text-on-surface">Risks you accept:</strong></p>
          <ul class="list-disc list-inside space-y-1 pl-2">
            <li>Trading losses — the strategy can lose money in any given period.</li>
            <li>Smart contract risk — code bugs could lock or drain funds.</li>
            <li>Platform risk — Hyperliquid or RPC outages can delay withdrawals.</li>
            <li>Deposits are uninsured and not guaranteed.</li>
          </ul>
          <p><strong class="text-on-surface">Fees:</strong> 20% on realized profits. First depositor to a new vault pays a one-time ~1 USDC activation fee (Hyperliquid requirement).</p>
          <p><strong class="text-on-surface">Withdrawals:</strong> 60-minute lock after any deposit. Withdrawals are two-step (request → wait → claim). Full detail in the Terms.</p>
          <p>Full legal text: <a class="text-primary underline" href="terms.html" target="_blank">Terms of Service</a> and <a class="text-primary underline" href="privacy.html" target="_blank">Privacy Policy</a>.</p>
        </div>
        <div class="mt-5 border-t border-outline-variant/20 pt-4">
          <label class="flex items-start gap-2 cursor-pointer">
            <input id="tc-checkbox" type="checkbox" class="mt-1" />
            <span class="text-sm text-secondary/80">
              I have read and agree to the Terms of Service and Privacy Policy, and I understand that I may lose some or all of my deposit.
            </span>
          </label>
          <div class="mt-4 flex gap-2 justify-end">
            <button id="tc-cancel" class="px-4 py-2 text-sm border border-outline-variant/20 rounded-lg text-on-surface hover:bg-outline-variant/10">Cancel</button>
            <button id="tc-accept" class="px-4 py-2 text-sm bg-primary text-on-primary rounded-lg font-headline disabled:opacity-40 disabled:cursor-not-allowed" disabled>Accept &amp; Deposit</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const cb = overlay.querySelector("#tc-checkbox");
    const acceptBtn = overlay.querySelector("#tc-accept");
    const cancelBtn = overlay.querySelector("#tc-cancel");
    cb.addEventListener("change", () => { acceptBtn.disabled = !cb.checked; });
    const cleanup = (result) => { overlay.remove(); resolve(result); };
    acceptBtn.addEventListener("click", () => cleanup(true));
    cancelBtn.addEventListener("click", () => cleanup(false));
    overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(false); });
  });
}
